import {
  computed,
  inject,
  Injectable,
  signal,
  Signal,
  WritableSignal,
} from '@angular/core';
import { TicketCategoryStore } from '../api/ticket-category/ticket-category-store.service';
import { SortGroupsWithTicketDataStore } from '../api/sort-group-with-ticket-data/sort-groups-ticket-data.store';
import {
  ETicketType,
  ISortGroupWithTicketData,
  ITicketCategory,
  TicketCategoryId,
  UNASSIGNED_TICKET_LIST,
} from '@web/dtos';

@Injectable({ providedIn: 'root' })
export class CmsTicketCategoryService {
  private readonly ticketCategoryStore: TicketCategoryStore =
    inject(TicketCategoryStore);
  private readonly sortGroupsStore: SortGroupsWithTicketDataStore = inject(
    SortGroupsWithTicketDataStore
  );
  private readonly isSaving: WritableSignal<boolean> = signal(false);

  public readonly isLoading: Signal<boolean> = computed(
    () =>
      this.ticketCategoryStore.isLoading() ||
      this.sortGroupsStore.isLoading() ||
      this.isSaving()
  );
  public readonly isDirty: Signal<boolean> = computed(() =>
    this.ticketCategoryStore.ticketCategoryPageDirtyMark()
  );
  public readonly hasChanges: Signal<boolean> = this.isDirty;

  public readonly fixedPriceTicketGroups: Signal<ISortGroupWithTicketData[]> =
    computed(() =>
      this.sortGroupsStore
        .sortGroups()
        .filter(
          (group: ISortGroupWithTicketData): boolean =>
            group.sortengruppentyp === 'VerkaufssortengruppeFestpreis'
        )
    );
  public readonly fixPriceTicketGroups: Signal<ISortGroupWithTicketData[]> =
    this.fixedPriceTicketGroups;
  public readonly relationTicketGroups: Signal<ISortGroupWithTicketData[]> =
    computed(() =>
      this.sortGroupsStore
        .sortGroups()
        .filter(
          (group: ISortGroupWithTicketData): boolean =>
            group.sortengruppentyp === 'VerkaufssortengruppeRelation'
        )
    );
  public readonly ticketCategories: Signal<ITicketCategory[]> = computed(() =>
    this.ticketCategoryStore.sortedTicketCategories()
  );
  public readonly relationTicketCategories: Signal<ITicketCategory[]> =
    computed(() => this.ticketCategoryStore.sortedRelationTicketCategories());
  public readonly relationTicketCategory: Signal<ITicketCategory[]> =
    this.relationTicketCategories;
  public readonly fixedPriceTicketCategories: Signal<ITicketCategory[]> =
    computed(() => this.ticketCategoryStore.sortedDirectTicketCategories());
  public readonly fixPriceTicketCategory: Signal<ITicketCategory[]> =
    this.fixedPriceTicketCategories;
  public readonly unassignedFixedPriceTicketGroups: Signal<
    ISortGroupWithTicketData[]
  > = computed(() =>
    this.fixedPriceTicketGroups().filter(
      (sortGroup: ISortGroupWithTicketData): boolean =>
        !sortGroup.ticketData?.ticketCategoryId
    )
  );
  public readonly unassignedRelationTicketGroups: Signal<
    ISortGroupWithTicketData[]
  > = computed(() =>
    this.relationTicketGroups().filter(
      (sortGroup: ISortGroupWithTicketData): boolean =>
        !sortGroup.ticketData?.ticketCategoryId
    )
  );
  public readonly connectedDropListIds: Signal<string[]> = computed(() =>
    this.ticketCategoryStore.connectedDropListIds()
  );

  public async save(
    ticketCategories: ITicketCategory[] = this.ticketCategories()
  ): Promise<void> {
    if (this.isSaving()) {
      return;
    }

    this.isSaving.set(true);

    try {
      await this.ticketCategoryStore.save(ticketCategories);
      await this.sortGroupsStore.saveTicketData();
      await this.ticketCategoryStore.deleteMarkedCategories();

      this.ticketCategoryStore.ticketCategoryPageDirtyMark.set(false);
      this.reload();
    } finally {
      this.isSaving.set(false);
    }
  }

  public addTicketCategory(name: string, ticketType: ETicketType): void {
    const trimmedName: string = name.trim();

    if (!trimmedName) {
      return;
    }

    const position: number = this.ticketCategoryStore
      .writableTicketCategories()
      .filter(
        (ticketCategory: ITicketCategory): boolean =>
          ticketCategory.ticketType === ticketType
      ).length;

    this.ticketCategoryStore.writableTicketCategories.update(
      (ticketCategories: ITicketCategory[]): ITicketCategory[] => [
        ...ticketCategories,
        {
          id: crypto.randomUUID() as TicketCategoryId,
          name: trimmedName,
          position,
          ticketType,
        },
      ]
    );
    this.markDirty();
  }

  public renameTicketCategory(ticketCategoryId: string, name: string): void {
    const trimmedName: string = name.trim();

    if (!trimmedName) {
      return;
    }

    let wasRenamed = false;
    this.ticketCategoryStore.writableTicketCategories.update(
      (ticketCategories: ITicketCategory[]): ITicketCategory[] =>
        ticketCategories.map(
          (ticketCategory: ITicketCategory): ITicketCategory => {
            if (
              ticketCategory.id !== ticketCategoryId ||
              ticketCategory.name === trimmedName
            ) {
              return ticketCategory;
            }

            wasRenamed = true;
            return {
              ...ticketCategory,
              name: trimmedName,
            };
          }
        )
    );

    if (wasRenamed) {
      this.markDirty();
    }
  }

  public moveTicketCategory(
    ticketType: ETicketType,
    previousIndex: number,
    currentIndex: number
  ): void {
    const categoriesOfType: ITicketCategory[] =
      this.getTicketCategoriesByType(ticketType);

    if (
      previousIndex < 0 ||
      currentIndex < 0 ||
      previousIndex >= categoriesOfType.length ||
      currentIndex >= categoriesOfType.length ||
      previousIndex === currentIndex
    ) {
      return;
    }

    const reorderedCategories: ITicketCategory[] = [...categoriesOfType];
    const [movedCategory] = reorderedCategories.splice(previousIndex, 1);
    reorderedCategories.splice(currentIndex, 0, movedCategory);
    const positionsByCategoryId: Map<string, number> = new Map(
      reorderedCategories.flatMap(
        (ticketCategory: ITicketCategory, index: number): [string, number][] =>
          ticketCategory.id ? [[ticketCategory.id, index]] : []
      )
    );

    this.ticketCategoryStore.writableTicketCategories.update(
      (ticketCategories: ITicketCategory[]): ITicketCategory[] =>
        ticketCategories.map(
          (ticketCategory: ITicketCategory): ITicketCategory => {
            const position: number | undefined = ticketCategory.id
              ? positionsByCategoryId.get(ticketCategory.id)
              : undefined;

            return position === undefined
              ? ticketCategory
              : { ...ticketCategory, position };
          }
        )
    );
    this.markDirty();
  }

  public switchTicketCategoryPosition(
    ticketCategoryId: string,
    direction: number
  ): void {
    const ticketCategory: ITicketCategory | undefined = this.ticketCategoryStore
      .writableTicketCategories()
      .find(
        (currentTicketCategory: ITicketCategory): boolean =>
          currentTicketCategory.id === ticketCategoryId
      );

    if (!ticketCategory) {
      return;
    }

    const categoriesOfType: ITicketCategory[] = this.getTicketCategoriesByType(
      ticketCategory.ticketType
    );
    const previousIndex: number = categoriesOfType.findIndex(
      (currentTicketCategory: ITicketCategory): boolean =>
        currentTicketCategory.id === ticketCategoryId
    );

    this.moveTicketCategory(
      ticketCategory.ticketType,
      previousIndex,
      previousIndex + direction
    );
  }

  public deleteTicketCategory(ticketCategory: ITicketCategory): void {
    const ticketCategoryId: TicketCategoryId | undefined = ticketCategory.id;

    if (!ticketCategoryId) {
      return;
    }

    this.sortGroupsStore.removeTicketCategory(ticketCategoryId);

    this.ticketCategoryStore.writableTicketCategories.update(
      (ticketCategories: ITicketCategory[]): ITicketCategory[] =>
        this.normalizeTicketCategoryPositions(
          ticketCategories.filter(
            (currentTicketCategory: ITicketCategory): boolean =>
              currentTicketCategory.id !== ticketCategoryId
          ),
          ticketCategory.ticketType
        )
    );
    this.ticketCategoryStore.markForDeletion(ticketCategoryId);
    this.markDirty();
  }

  public deleteTicketCategories(ticketCategory: ITicketCategory): void {
    this.deleteTicketCategory(ticketCategory);
  }

  public getTicketGroupsForCategory(
    ticketCategoryId: string
  ): ISortGroupWithTicketData[] {
    return this.sortGroupsStore
      .sortGroups()
      .filter(
        (sortGroup: ISortGroupWithTicketData): boolean =>
          sortGroup.ticketData?.ticketCategoryId === ticketCategoryId
      )
      .sort(
        (
          firstSortGroup: ISortGroupWithTicketData,
          secondSortGroup: ISortGroupWithTicketData
        ): number =>
          (firstSortGroup.ticketData?.positionInCategory ?? 0) -
          (secondSortGroup.ticketData?.positionInCategory ?? 0)
      );
  }

  public getConnectedDropListIds(ticketType: ETicketType): string[] {
    return [
      this.getUnassignedDropListId(ticketType),
      ...this.getTicketCategoriesByType(ticketType).flatMap(
        (ticketCategory: ITicketCategory): string[] =>
          ticketCategory.id ? [`category-list-${ticketCategory.id}`] : []
      ),
    ];
  }

  public getUnassignedDropListId(ticketType: ETicketType): string {
    return `${UNASSIGNED_TICKET_LIST}-${ticketType}`;
  }

  public assignTicketToCategory(
    ticketGroup: ISortGroupWithTicketData,
    ticketCategoryId: string,
    position?: number
  ): void {
    this.sortGroupsStore.assignTicketToCategory(
      ticketGroup,
      ticketCategoryId,
      position
    );
    this.markDirty();
  }

  public assignTicketsToCategory(
    ticketGroups: ISortGroupWithTicketData[],
    ticketCategoryId: string
  ): void {
    if (ticketGroups.length === 0) {
      return;
    }

    this.sortGroupsStore.assignTicketsToCategory(
      ticketGroups,
      ticketCategoryId
    );
    this.markDirty();
  }

  public removeTicketFromCategory(ticketGroup: ISortGroupWithTicketData): void {
    this.sortGroupsStore.removeTicketFromCategory(ticketGroup);
    this.markDirty();
  }

  public moveTicketInCategory(
    ticketGroup: ISortGroupWithTicketData,
    direction: number
  ): void {
    this.sortGroupsStore.moveTicketInCategory(ticketGroup, direction);
    this.markDirty();
  }

  public resetChanges(): void {
    this.ticketCategoryStore.reset();
    this.sortGroupsStore.reset();
  }

  public reload(): void {
    this.ticketCategoryStore.reload();
    this.sortGroupsStore.reloadSortGroups();
  }

  private getTicketCategoriesByType(
    ticketType: ETicketType
  ): ITicketCategory[] {
    return this.ticketCategoryStore
      .writableTicketCategories()
      .filter(
        (ticketCategory: ITicketCategory): boolean =>
          ticketCategory.ticketType === ticketType
      )
      .sort(
        (
          firstTicketCategory: ITicketCategory,
          secondTicketCategory: ITicketCategory
        ): number =>
          firstTicketCategory.position - secondTicketCategory.position
      );
  }

  private normalizeTicketCategoryPositions(
    ticketCategories: ITicketCategory[],
    ticketType: ETicketType
  ): ITicketCategory[] {
    const positionsByCategoryId: Map<string, number> = new Map(
      ticketCategories
        .filter(
          (ticketCategory: ITicketCategory): boolean =>
            ticketCategory.ticketType === ticketType
        )
        .sort(
          (
            firstTicketCategory: ITicketCategory,
            secondTicketCategory: ITicketCategory
          ): number =>
            firstTicketCategory.position - secondTicketCategory.position
        )
        .flatMap(
          (
            ticketCategory: ITicketCategory,
            index: number
          ): [string, number][] =>
            ticketCategory.id ? [[ticketCategory.id, index]] : []
        )
    );

    return ticketCategories.map(
      (ticketCategory: ITicketCategory): ITicketCategory => {
        const position: number | undefined = ticketCategory.id
          ? positionsByCategoryId.get(ticketCategory.id)
          : undefined;

        return position === undefined
          ? ticketCategory
          : { ...ticketCategory, position };
      }
    );
  }

  private markDirty(): void {
    this.ticketCategoryStore.ticketCategoryPageDirtyMark.set(true);
  }
}
